import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { surveysService } from "../services/surveys";
import { DistributionMode } from "@clawplete/shared/types";
import api from "../services/api";

interface MarketRate {
  highest: number;
  average: number;
  median: number;
}

interface Question {
  id: string;
  question_text: string;
  options: string[];
}

export default function SurveyCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [targetResponses, setTargetResponses] = useState(100);
  const [distributionMode, setDistributionMode] = useState<DistributionMode>(DistributionMode.EFFICIENT);
  const [questions, setQuestions] = useState<Question[]>([
    { id: crypto.randomUUID(), question_text: "", options: ["", ""] },
  ]);
  const [error, setError] = useState("");

  // Fetch market rate for budget guidance
  const { data: marketRateData } = useQuery<{ market_rates: MarketRate }>({
    queryKey: ["market-rates"],
    queryFn: async () => {
      const res = await api.get("/market-rates");
      return res.data.data;
    },
  });

  const marketRate = marketRateData?.market_rates;

  const createMutation = useMutation({
    mutationFn: surveysService.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      navigate(`/surveys/${data.survey.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || "Failed to create survey");
    },
  });

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { id: crypto.randomUUID(), question_text: "", options: ["", ""] },
    ]);
  };

  const removeQuestion = (id: string) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((q) => q.id !== id));
    }
  };

  const updateQuestionText = (id: string, text: string) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, question_text: text } : q))
    );
  };

  const addOption = (questionId: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId ? { ...q, options: [...q.options, ""] } : q
      )
    );
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.filter((_, i) => i !== optionIndex) }
          : q
      )
    );
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((opt, i) =>
                i === optionIndex ? value : opt
              ),
            }
          : q
      )
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!budget || parseFloat(budget) <= 0) {
      setError("Budget must be greater than 0");
      return;
    }
    if (targetResponses < 1) {
      setError("Target responses must be at least 1");
      return;
    }

    const validQuestions = questions.filter((q) => q.question_text.trim());
    if (validQuestions.length === 0) {
      setError("At least one question is required");
      return;
    }

    // Validate first question (API only supports single question)
    const firstQuestion = validQuestions[0];
    const validOptions = firstQuestion.options.filter((o) => o.trim());
    if (validOptions.length < 2) {
      setError("Question must have at least 2 options");
      return;
    }

    // Validate minimum target responses (5x number of options)
    const minTargetResponses = validOptions.length * 5;
    if (targetResponses < minTargetResponses) {
      setError(`Target responses must be at least ${minTargetResponses} for ${validOptions.length} options`);
      return;
    }

    // Prepare data
    // Note: API expects options as string[], not array of objects
    const surveyData = {
      title: title.trim(),
      description: description.trim() || undefined,
      budget,
      target_responses: targetResponses,
      distribution_mode: distributionMode,
      question: {
        question_text: validQuestions[0].question_text.trim(),
        options: validQuestions[0].options
          .filter((o) => o.trim()),
      },
    };

    createMutation.mutate(surveyData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Create Survey</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                id="title"
                type="text"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter survey title"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                id="description"
                className="input"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter survey description (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                  Budget (USDC) *
                </label>
                <input
                  id="budget"
                  type="number"
                  className="input"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="100"
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label htmlFor="targetResponses" className="block text-sm font-medium text-gray-300 mb-2">
                  Target Responses *
                </label>
                <input
                  id="targetResponses"
                  type="number"
                  className="input"
                  value={targetResponses}
                  onChange={(e) => setTargetResponses(parseInt(e.target.value) || 1)}
                  min="1"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="distributionMode" className="block text-sm font-medium text-gray-300 mb-2">
                Distribution Mode
              </label>
              <select
                id="distributionMode"
                className="input"
                value={distributionMode}
                onChange={(e) => setDistributionMode(e.target.value as DistributionMode)}
              >
                <option value={DistributionMode.EFFICIENT}>Efficient</option>
                <option value={DistributionMode.ASAP}>ASAP</option>
                <option value={DistributionMode.ECO}>Eco</option>
              </select>
              <p className="mt-1 text-sm text-gray-400">
                {distributionMode === DistributionMode.EFFICIENT && "Balanced distribution for optimal results"}
                {distributionMode === DistributionMode.ASAP && "Fastest distribution possible"}
                {distributionMode === DistributionMode.ECO && "Lowest cost distribution"}
              </p>
            </div>

            {/* Market Rate Reference */}
            {marketRate && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <h3 className="font-medium text-blue-400">Current Market Rates</h3>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-800 rounded-md p-3 shadow-sm">
                    <p className="text-xs text-gray-400">Highest</p>
                    <p className="text-lg font-bold text-green-400">{marketRate.highest.toFixed(1)}</p>
                    <p className="text-xs text-gray-400">USDC/resp</p>
                  </div>
                  <div className="bg-gray-800 rounded-md p-3 shadow-sm">
                    <p className="text-xs text-gray-400">Average</p>
                    <p className="text-lg font-bold text-blue-400">{marketRate.average.toFixed(1)}</p>
                    <p className="text-xs text-gray-400">USDC/resp</p>
                  </div>
                  <div className="bg-gray-800 rounded-md p-3 shadow-sm">
                    <p className="text-xs text-gray-400">Median</p>
                    <p className="text-lg font-bold text-purple-400">{marketRate.median.toFixed(1)}</p>
                    <p className="text-xs text-gray-400">USDC/resp</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-blue-300">
                  Set your rate above the median for faster responses.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Questions */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-100">Questions</h2>
            <button
              type="button"
              onClick={addQuestion}
              className="btn btn-secondary"
            >
              Add Question
            </button>
          </div>

          <div className="space-y-6">
            {questions.map((question, qIndex) => (
              <div key={question.id} className="p-4 border border-dark-border rounded-lg bg-dark-800">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium text-gray-100">Question {qIndex + 1}</h3>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(question.id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Question Text *
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={question.question_text}
                      onChange={(e) => updateQuestionText(question.id, e.target.value)}
                      placeholder="Enter your question"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-300">
                        Options * (at least 2)
                      </label>
                      {question.options.length < 10 && (
                        <button
                          type="button"
                          onClick={() => addOption(question.id)}
                          className="text-primary-300 hover:text-primary-200 text-sm"
                        >
                          Add Option
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex gap-2">
                          <input
                            type="text"
                            className="input flex-1"
                            value={option}
                            onChange={(e) => updateOption(question.id, oIndex, e.target.value)}
                            placeholder={`Option ${oIndex + 1}`}
                          />
                          {question.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(question.id, oIndex)}
                              className="text-red-400 hover:text-red-300 px-2"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        {(budget && targetResponses) && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-100 mb-4">Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-gray-300">
                <span>Total Budget:</span>
                <span className="font-medium">{parseFloat(budget).toFixed(2)} USDC</span>
              </div>
              <div className="flex justify-between text-gray-300">
                <span>Target Responses:</span>
                <span className="font-medium">{targetResponses}</span>
              </div>
              <div className="flex justify-between items-center text-gray-300">
                <span>Reward per response:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-lg">
                    {(parseFloat(budget) / targetResponses).toFixed(2)} USDC
                  </span>
                  {marketRate && parseFloat(budget) > 0 && (
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      (parseFloat(budget) / targetResponses) >= marketRate.highest
                        ? 'bg-green-500/20 text-green-400'
                        : (parseFloat(budget) / targetResponses) >= marketRate.median
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {(parseFloat(budget) / targetResponses) >= marketRate.highest
                        ? '★ Above highest'
                        : (parseFloat(budget) / targetResponses) >= marketRate.median
                        ? '● Above median'
                        : '▽ Below median'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/surveys")}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Survey"}
          </button>
        </div>
      </form>
    </div>
  );
}
